import type { PromptTemplateId } from "@pluck/api/lib/board/compose";
import { Handle, type NodeProps, Position } from "@xyflow/react";
import { Loader2, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
	ASPECT_RATIOS,
	type AspectRatio,
	AUTO_MODEL,
	effectivePromptMode,
	effectivePromptModel,
	LOCAL_MODEL,
	PROMPT_NODE_WIDTH,
	type PromptMode,
	type PromptNode,
	type QualityTier,
} from "@/lib/board-graph";
import {
	type HostedModelInfo,
	pickAutoModel,
	resolveModelQuality,
	runCredits,
	TIER_LABELS,
	templateAspect,
} from "@/lib/hosted-models";
import { cn } from "@/lib/utils";
import { useBoardData } from "./board-context";

const MODE_OPTIONS: { value: PromptMode; label: (count: number) => string }[] =
	[
		{ value: "each", label: (count) => `${count} separate` },
		{ value: "combine", label: () => "1 combined" },
	];

/** With several generations connected: one run per image, or one merged design. */
function ModeToggle({
	count,
	mode,
	name,
	onChange,
}: {
	count: number;
	mode: PromptMode;
	name: string;
	onChange: (mode: PromptMode) => void;
}) {
	return (
		<fieldset className="nodrag grid grid-cols-2 gap-1 rounded-md bg-muted p-0.5">
			<legend className="sr-only">How to use the connected generations</legend>
			{MODE_OPTIONS.map((option) => (
				<label
					className={cn(
						"cursor-pointer rounded-sm px-2 py-1 text-center text-[11px] transition-colors has-focus-visible:ring-2 has-focus-visible:ring-ring",
						mode === option.value
							? "bg-background font-medium shadow-xs"
							: "text-muted-foreground hover:text-foreground"
					)}
					key={option.value}
				>
					<input
						checked={mode === option.value}
						className="sr-only"
						name={name}
						onChange={() => onChange(option.value)}
						type="radio"
						value={option.value}
					/>
					{option.label(count)}
				</label>
			))}
		</fieldset>
	);
}

function ModelPicker({
	value,
	isDesktop,
	models,
	onChange,
}: {
	isDesktop: boolean;
	models: HostedModelInfo[];
	onChange: (model: string) => void;
	value: string;
}) {
	return (
		<Select onValueChange={onChange} value={value}>
			<SelectTrigger
				aria-label="Model"
				className="nodrag h-7 w-full text-[11px]"
			>
				<SelectValue />
			</SelectTrigger>
			<SelectContent>
				{isDesktop && (
					<SelectItem value={LOCAL_MODEL}>Local Codex · free</SelectItem>
				)}
				{models.length > 0 && (
					<SelectItem value={AUTO_MODEL}>
						Auto · best model for the brief
					</SelectItem>
				)}
				{models.map((m) => (
					<SelectItem key={m.id} value={m.id}>
						{m.label} · {m.credits} credits
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
}

interface PromptSettings {
	aspect?: AspectRatio;
	quality?: QualityTier;
}

/** The hosted model a prompt will run on (Auto resolved), or undefined. */
function pickedModel(
	model: string,
	models: HostedModelInfo[],
	prompt: string,
	referencesPerRun: number
): HostedModelInfo | undefined {
	if (model === LOCAL_MODEL) {
		return;
	}
	return model === AUTO_MODEL
		? pickAutoModel(models, prompt, referencesPerRun)
		: models.find((m) => m.id === model);
}

/** Credits a run of this prompt will cost, or null for local runs. */
function costFor(
	picked: HostedModelInfo | undefined,
	settings: PromptSettings,
	template: PromptTemplateId,
	runs: number
): { credits: number; label: string } | null {
	if (!picked) {
		return null;
	}
	const quality = resolveModelQuality(picked, settings.quality);
	const tier = picked.qualities.find((q) => q.id === quality);
	const detail = picked.qualities.length > 1 && tier ? ` · ${tier.label}` : "";
	return {
		credits: runCredits(picked, settings, template) * runs,
		label: `${picked.label}${detail}`,
	};
}

const ASPECT_LABELS: Record<AspectRatio, string> = {
	auto: "Auto",
	"1:1": "1:1 Square",
	"16:9": "16:9 Wide",
	"9:16": "9:16 Tall",
	"4:3": "4:3",
	"3:4": "3:4",
	"3:2": "3:2",
	"2:3": "2:3",
	"21:9": "21:9 Ultrawide",
};

/** Aspect ratio and quality for hosted runs. */
function RunSettingsPicker({
	picked,
	settings,
	template,
	onChange,
}: {
	picked: HostedModelInfo | undefined;
	settings: PromptSettings;
	template: PromptTemplateId;
	onChange: (settings: PromptSettings) => void;
}) {
	const autoAspect = templateAspect(template);
	const qualities: { id: QualityTier; label: string }[] = picked
		? picked.qualities.map((q) => ({
				id: q.id,
				label:
					q.label === "Standard"
						? TIER_LABELS[q.id]
						: `${TIER_LABELS[q.id]} · ${q.label}`,
			}))
		: (Object.keys(TIER_LABELS) as QualityTier[]).map((id) => ({
				id,
				label: TIER_LABELS[id],
			}));
	const quality = picked
		? resolveModelQuality(picked, settings.quality)
		: (settings.quality ?? "standard");
	return (
		<div className="grid grid-cols-2 gap-1.5">
			<Select
				onValueChange={(next) => onChange({ aspect: next as AspectRatio })}
				value={settings.aspect ?? "auto"}
			>
				<SelectTrigger
					aria-label="Aspect ratio"
					className="nodrag h-7 w-full text-[11px]"
				>
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					{ASPECT_RATIOS.map((aspect) => (
						<SelectItem key={aspect} value={aspect}>
							{aspect === "auto"
								? `Auto · ${autoAspect === "auto" ? "match input" : autoAspect}`
								: ASPECT_LABELS[aspect]}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
			<Select
				disabled={qualities.length < 2}
				onValueChange={(next) => onChange({ quality: next as QualityTier })}
				value={quality}
			>
				<SelectTrigger
					aria-label="Quality"
					className="nodrag h-7 w-full text-[11px]"
				>
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					{qualities.map((q) => (
						<SelectItem key={q.id} value={q.id}>
							{q.label}
							{picked
								? ` · ${runCredits(picked, { ...settings, quality: q.id }, template)} cr`
								: ""}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
		</div>
	);
}

function footerText(
	model: string,
	cost: { label: string } | null,
	balance: number | null,
	canRunLocally: boolean
): string {
	if (model === LOCAL_MODEL) {
		return canRunLocally
			? "Runs locally with Codex · free"
			: "Generation needs Pluck Desktop";
	}
	const left =
		balance === null
			? ""
			: ` · ${balance.toLocaleString("en-US")} credits left`;
	return `${cost?.label ?? "Hosted"}${left}`;
}

export function PromptNodeView({ id, data, selected }: NodeProps<PromptNode>) {
	const {
		onUpdatePromptText,
		onGenerate,
		onRemoveNode,
		runningPromptNodeIds,
		canGenerate,
		onSetPromptMode,
		onSetPromptModel,
		onSetPromptSettings,
		promptTemplate,
		promptGenerationInputs,
		promptRunShapes,
		hostedModels,
		isDesktop,
		creditBalance,
	} = useBoardData();
	const running = runningPromptNodeIds.has(id);
	const generationInputs = promptGenerationInputs.get(id) ?? 0;
	const mode = effectivePromptMode(data);
	const model = effectivePromptModel(data, {
		desktop: isDesktop,
		hostedAvailable: hostedModels.length > 0,
	});
	const shape = promptRunShapes.get(id) ?? { runs: 1, referencesPerRun: 0 };
	const settings: PromptSettings = {
		aspect: data.aspect,
		quality: data.quality,
	};
	const picked = pickedModel(
		model,
		hostedModels,
		data.text,
		shape.referencesPerRun
	);
	const cost = costFor(picked, settings, promptTemplate, shape.runs);
	const canRun = model === LOCAL_MODEL ? canGenerate : cost !== null;
	let buttonLabel = "Copy prompt";
	if (running) {
		buttonLabel = "Generating…";
	} else if (canRun) {
		buttonLabel = cost ? `Generate · ${cost.credits}` : "Generate";
	}

	return (
		<div
			className={cn(
				"group flex flex-col gap-2 rounded-xl border bg-card p-3 shadow-sm",
				selected && "ring-2 ring-ring"
			)}
			style={{ width: PROMPT_NODE_WIDTH }}
		>
			<Handle position={Position.Left} type="target" />
			<div className="flex items-center gap-2">
				<Sparkles className="size-4 text-muted-foreground" />
				<p className="flex-1 font-medium text-xs">Prompt</p>
				<Button
					aria-label="Remove prompt"
					className="opacity-0 group-hover:opacity-100"
					onClick={() => onRemoveNode(id)}
					size="icon-sm"
					variant="ghost"
				>
					<X />
				</Button>
			</div>
			<Textarea
				className="nodrag nowheel min-h-24 resize-y text-xs"
				onChange={(e) => onUpdatePromptText(id, e.target.value)}
				placeholder="Describe the design you want. Connect plucks on the left as references."
				value={data.text}
			/>
			{(isDesktop || hostedModels.length > 0) && (
				<ModelPicker
					isDesktop={isDesktop}
					models={hostedModels}
					onChange={(next) => onSetPromptModel(id, next)}
					value={model}
				/>
			)}
			{model !== LOCAL_MODEL && hostedModels.length > 0 && (
				<RunSettingsPicker
					onChange={(next) => onSetPromptSettings(id, next)}
					picked={picked}
					settings={settings}
					template={promptTemplate}
				/>
			)}
			{generationInputs > 1 && (
				<ModeToggle
					count={generationInputs}
					mode={mode}
					name={`prompt-mode-${id}`}
					onChange={(next) => onSetPromptMode(id, next)}
				/>
			)}
			<div className="flex items-center justify-between gap-2">
				<p className="min-w-0 truncate text-[11px] text-muted-foreground">
					{footerText(model, cost, creditBalance, canGenerate)}
				</p>
				<Button
					className="nodrag"
					disabled={running}
					onClick={() => onGenerate(id)}
					size="sm"
				>
					{running ? <Loader2 className="animate-spin" /> : <Sparkles />}
					{buttonLabel}
				</Button>
			</div>
			<Handle position={Position.Right} type="source" />
		</div>
	);
}
